import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  MenuItem,
  Paper,
  Button,
  Select,
  InputLabel,
  FormControl
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import DetailsPageLowerLayer from '../DetailsPageLowerLayer/DetailsPageLowerLayer';
import TopSection from '../../CommonComponents/TopSection';
import { fetchUserRequestData, fetchApproverRequestStatuses, updateApproverAction } from '../../services/UserRole/RequestStatusService';
import { APPLICATION_LIST_ALL, APPLICATION_TYPE_MAP, APPLICATION_TYPE_MAP_REPORT } from '../../constants/userAccess';
import ErrorPage from '../ErrorPage';
import TrackAccessRequestModal from '../DetailsPageLowerLayer/TrackAccessRequestModal';
import { useAccessToken } from "../../auth/useAccessToken";
import { useApplication } from "../../context/ApplicationContext";

const formatDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};
const INPUT_WIDTH = 450;

const TrackAccessRequest = () => {
  const breadcrumbArray = ["User Access Management", "Request Status"];
  const [toolbarStatus, setToolbarStatus] = useState("");
  const [userRequestRows, setUserRequestRows] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [approverData, setApproverData] = useState([]);
  const [loadingApprover, setLoadingApprover] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedApp, setSelectedApp] = useState("All");
  const [openRequestModal, setOpenRequestModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalRows, setModalRows] = useState([]);
  const accessToken = useAccessToken();
  const { selectedApplication, setSelectedApplication } = useApplication();
  const [decoded, setDecoded] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const isSSA = selectedApp === "IO - Safety Stock Approval";
  const hasSelection = selectedRows.length > 0;
  const gridApiRef = useRef(null);

  useEffect(() => {
  if (selectedApplication) {
    setSelectedApp(selectedApplication);
  }
}, []);

useEffect(() => {
  if (selectedApp && selectedApp !== "All") {
    setSelectedApplication(selectedApp);
  }
}, [selectedApp]);

  const handleRequestIdClick = useCallback((requestId) => {
    setOpenRequestModal(true);
    setModalLoading(true);

    const parentRequest = userRequestRows.find(
      (r) => r.requestId === requestId
    );

    const modalData = (parentRequest?.userRequestStatuses || []).map(
      (item, index) => ({
        id: index,
        priority: item.accessRequestPriority,
        requestId,
        requestedUser: item.requestedUser,
        approvalWorkflow: item.approvalWorkflow,
        workflowType: item.approvalWorkflowType,
        workflowApprover: item.workflowApprover,
        approvalStatus: item.approvalStatus,
        referenceId: item.referenceId,
        ticketId: item.ticketId,
        region: item.region,
        lastUpdatedOn: item.modifiedDatetime
      })
    );
    setModalRows(modalData);
    setModalLoading(false);
  }, [userRequestRows]);

  const handleCloseModal = () => {
    setOpenRequestModal(false);
    setModalRows([]);
  };

  const modalColumns = useMemo(() => ([
    { field: 'priority', headerName: 'Priority', width: 90 },
    { field: 'requestId', headerName: 'Request ID', flex: 1 },
    { field: 'requestedUser', headerName: 'Requested User', flex: 1 },
    { field: 'approvalWorkflow', headerName: 'Approval Workflow', flex: 1 },
    { field: 'workflowType', headerName: 'Approval Workflow Type', flex: 1 },
    { field: 'workflowApprover', headerName: 'Workflow Approver', flex: 1 },
    { field: 'approvalStatus', headerName: 'Approval Status', flex: 1 },
    {
      field: 'referenceId',
      headerName: 'Ticket ID',
      flex: 1,
      cellRenderer: (params) => {
        const url = params.data?.ticketId;
        const referenceId = params.value;

        if (!referenceId) return "";
        if (!url) return referenceId;

        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1976d2', textDecoration: 'underline' }}
            onClick={(e) => e.stopPropagation()}
          >
            {referenceId}
          </a>
        );
      }
    },
    { field: 'region', headerName: 'Region', flex: 1 }
  ]), []);

  const handleApprove = async (row) => {
    try {
      const payload = {
        accessRequestId: row.accessRequestId,
        requestId: row.requestId,
        action: "APPROVE",
        approvalType: APPLICATION_TYPE_MAP_REPORT[selectedApp]
      };
      await updateApproverAction(payload);

      setApproverData(prev =>
        prev.map(item =>
          item.accessRequestId === row.accessRequestId
            ? { ...item, status: "APPROVED" }
            : item
        )
      );
    } catch (error) {
      console.error("Approve failed", error);
    }
  };

  const handleReject = async (row) => {
    try {
      const payload = {
        accessRequestId: row.accessRequestId,
        requestId: row.requestId,
        action: "REJECT"
      };
      await updateApproverAction(payload);

      setApproverData(prev =>
        prev.map(item =>
          item.accessRequestId === row.accessRequestId
            ? { ...item, status: "REJECTED" }
            : item
        )
      );
    } catch (error) {
      console.error("Reject failed", error);
    }
  };

  const handleAction = async (rows, action) => {
    try {
      const selected = Array.isArray(rows) ? rows : [rows];

      const payload = {
        accessRequestId: selected.map((row) =>
          String(row.accessRequestId)
        ),
        action,
        approvalType: APPLICATION_TYPE_MAP_REPORT[selectedApp]
      };

      await updateApproverAction(payload);

      setApproverData((prev) =>
        prev.map((item) =>
          payload.accessRequestId.includes(String(item.accessRequestId))
            ? {
              ...item,
              status: action === "APPROVE" ? "APPROVED" : "REJECTED"
            }
            : item
        )
      );
      setSelectedRows([]);
    } catch (error) {
      console.error(`${action} failed`, error);
    }
  };

  const approvalStatusOptions = useMemo(() => {
    return [...new Set(userRequestRows.map(r => r.status))];
  }, [userRequestRows]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    try {
      const parsed = jwtDecode(accessToken);
      setDecoded(parsed);
    } catch (err) {
      console.error("Failed to decode token:", err);
    }
  }, [accessToken]);
  const decodedUserName = decoded?.name || "";

  useEffect(() => {
    const fetchUserRequests = async () => {
      try {
        setLoadingUser(true);
        setUserRequestRows([]);
        setAccessDenied(false);
        const applicationType = APPLICATION_TYPE_MAP[selectedApp];
        const userRequestData = await fetchUserRequestData(applicationType);
        if (userRequestData?.accessDenied) {
          setAccessDenied(true);
          return;
        }
        const mappedData = (userRequestData?.userRequestStatuses || []).map((item) => ({
          requestId: item.requestId,
          userName: item.userName,
          requestedUser: item.requestedUser,
          //userId: item.userId,
          applicationType: item.applicationType,
          role: item.role,
          requestedDate: item.requestedDate,
          status: item.status,
          lastUpdatedDate: item.lastUpdateDate,
          userRequestStatuses: item.userRequestStatuses || []
        }));
        setUserRequestRows(mappedData);
      } catch (error) {
        console.error("Failed to fetch request status", error);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUserRequests();
  }, [selectedApp, decodedUserName]);

  useEffect(() => {
    const fetchApproverRequests = async () => {
      try {
        setLoadingApprover(true);
        setApproverData([]);
        const applicationType = APPLICATION_TYPE_MAP[selectedApp];
        const response = await fetchApproverRequestStatuses(applicationType);

        const mappedData = (response?.approverRequestStatuses || [])
          //.filter(item => !["APPROVED", "REJECTED"].includes((item.approvalStatus || "").toUpperCase()))
          .filter(item => (item.approvalWorkflowType || "").toUpperCase() !== "AD-TICKET")
          .map(item => ({
            accessRequestId: item.accessRequestId,
            requestId: item.requestId,
            user: item.requestedUser,
            approvalWorkflow: item.approvalWorkflow,
            workflowType: item.approvalWorkflowType,
            role: item.role,
            workflowApprover: item.workflowApprover,
            status: item.approvalStatus,
            referenceId: item.referenceId
          }));
        setApproverData(mappedData);
      } catch (error) {
        console.error("Failed to fetch approver requests", error);
      } finally {
        setLoadingApprover(false);
      }
    };
    fetchApproverRequests();
  }, [selectedApp]);

  const userTab = useMemo(() => ({
    label: `User`,
    loading: loadingUser,
    columns: [
      {
        field: 'requestId',
        headerName: 'Request ID',
        flex: 1,
        cellRenderer: (params) => (
          <span
            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={(e) => {
              e.stopPropagation();
              handleRequestIdClick(params.value);
            }}
          >
            {params.value}
          </span>
        )
      },
      { field: 'userName', headerName: 'User Name', flex: 1 },
      { field: 'applicationType', headerName: 'Application ID', flex: 1 },
      {
        field: 'requestedDate',
        headerName: 'Requested Date',
        flex: 1,
        sortable: true,
        sort: 'desc',                 // default on load
        comparator: (a, b) =>
          new Date(a) - new Date(b),   //  correct date sort
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        headerTooltip: "For more details, click on Hyperlink",
        headerClass: "status-header-info"
      },
      { field: 'lastUpdatedDate', headerName: 'Last Updated Date', flex: 1 }
    ],
    rows: toolbarStatus
      ? userRequestRows.filter(r => r.status === toolbarStatus) : userRequestRows,
    dropdowns: [
      {
        label: "Approval Status",
        options: approvalStatusOptions,
        onChange: setToolbarStatus,
      },
    ],
  }), [toolbarStatus, userRequestRows, approvalStatusOptions, loadingUser, handleRequestIdClick, isSSA]);

  const approverTab = useMemo(
    () => ({
      label: "Approver",
      loading: loadingApprover,
      rowSelection: "multiple",
      onGridReady: (params) => {
        gridApiRef.current = params.api;
      },
      onSelectionChanged: (params) => {
        const rows = params.api.getSelectedRows();
        setSelectedRows(rows);
      },
      toolbar: {
        buttons: [
          {
            label: "Approve",
            disabled: !hasSelection,
            onClick: async () => {
              await handleAction(selectedRows, "APPROVE");
            }
          },
          {
            label: "Reject",
            disabled: !hasSelection,
            onClick: async () => {
              await handleAction(selectedRows, "REJECT");
            }
          }
        ],
      },
      columns: [
        {
          headerName: '',
          field: 'checkbox',
          maxWidth: 50,
          checkboxSelection: (params) => {
            const status = (params.data?.status || "").toUpperCase();
            return !["APPROVED", "REJECTED", "COMPLETED"].includes(status);
          },
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
        },
        { field: "requestId", headerName: "Request ID", flex: 1 },
        { field: "user", headerName: "Requested User", flex: 1 },
        ...(isSSA
          ? [
            {
              field: 'role',
              headerName: 'Role',
              flex: 0.6,
            },
          ]
          : []),
        { field: 'approvalWorkflow', headerName: 'Approval Workflow', flex: 1 },
        { field: 'workflowType', headerName: 'Approval Workflow Type', flex: 1 },
        { field: 'workflowApprover', headerName: 'Workflow Approver', flex: 1 },
        { field: "status", headerName: "Status", flex: 1 },
        { field: 'referenceId', headerName: 'Reference ID', flex: 1 },
        {
          field: "action",
          headerName: "Action",
          flex: 1,
          cellRenderer: (params) => (
            <Box display="flex" mt={0.5} gap={1}>
              <Button
                size="small"
                variant="contained"
                sx={{
                  color: '#fff',
                  backgroundColor: '#2e7d32'
                }}
                disabled={hasSelection || params.data.status === "APPROVED" || params.data.status === "REJECTED"
                  || params.data.status === "COMPLETED"
                }
                onClick={() => handleAction(params.data, "APPROVE")}
              >
                Approve
              </Button>

              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: '#d32f2f',
                  borderColor: '#d32f2f'
                }}
                disabled={hasSelection || params.data.status === "APPROVED" || params.data.status === "REJECTED"
                  || params.data.status === "COMPLETED"
                }
                onClick={() => handleAction(params.data, "REJECT")}
              >
                Reject
              </Button>
            </Box>
          ),
        },
      ],
      rows: approverData,
    }),
    [approverData, loadingApprover, isSSA, hasSelection, selectedRows]
  );

  const tabs = [userTab, approverTab];

  return (
    <div>
      <TopSection breadcrumbArray={breadcrumbArray} hideHeader={true} headerName={""} hideTitle={true} />
      <Paper elevation={2} sx={{ mt: 0.5, mb: 3, pt: 1, pb: 1.5, bgcolor: '#ddd', height: 60 }}>
        <Box mt={2} mb={3} mx="auto" sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          width: INPUT_WIDTH
        }}>
          <FormControl size="small">
            <InputLabel id="application-label">Application</InputLabel>
            <Select
              labelId="application-label"
              id="application"
              value={selectedApp}
              label="Application"
              onChange={(e) => {
                const app = e.target.value;
                setSelectedApp(app);
                setSelectedApplication(app);
              }}
              sx={{ color: "#000" }}
            >
              {APPLICATION_LIST_ALL.map((app) => (
                <MenuItem key={app} value={app}>
                  {app}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>
      {accessDenied ? (
        <ErrorPage message="Access Denied: You do not have permission to view this page." />
      ) : (
        <DetailsPageLowerLayer tabs={tabs} />
      )}
      <TrackAccessRequestModal
        open={openRequestModal}
        handleClose={handleCloseModal}
        rows={modalRows}
        columns={modalColumns}
        loading={modalLoading}
      />
    </div>
  );
};

export default TrackAccessRequest;
